import { noop } from 'lodash';
import { createContext, useContext } from 'react';
import { API } from '@shared/types/api';

export interface ViewLayoutState {
  /** The unsaved order if there is one, else the saved layout. */
  layout: API.ViewTemplateLayoutFragment | null;
  /** True only while the layout's first fetch is in flight, with nothing to show yet. */
  isLoading: boolean;
  /** True when the unsaved order differs from the saved one. */
  isModified: boolean;
}

/**
 * No layout and nothing to wait for by default, so a card rendered outside a ViewLayoutProvider
 * follows workflow template order.
 */
const ViewLayoutContext = createContext<ViewLayoutState>({
  layout: null,
  isLoading: false,
  isModified: false,
});
export const ViewLayoutStateProvider = ViewLayoutContext.Provider;

export function useViewLayoutState(): ViewLayoutState {
  return useContext(ViewLayoutContext);
}

/** The layout a collection view orders its fields by, or `null` for workflow template order. */
export function useViewLayout(): API.ViewTemplateLayoutFragment | null {
  return useViewLayoutState().layout;
}

type ComponentOrderUpdater = (visibleIds: string[]) => string[];
/** Changes the order of the visible fields, starting from the order the view shows now. */
export type UpdateComponentOrder = (update: ComponentOrderUpdater) => void;

/**
 * Edits to the unsaved order of a view's fields. It is its own record, so whatever saves or
 * reverts the view template saves or reverts this alongside it. Each action keeps its identity
 * across renders.
 */
export interface ViewLayoutActions {
  updateComponentOrder: UpdateComponentOrder;
  revertLayout: VoidFunction;
  saveLayout: () => Promise<void>;
}

/** Outside a ViewLayoutProvider there is no order to edit, and nothing to save or revert. */
const ViewLayoutActionsContext = createContext<ViewLayoutActions>({
  updateComponentOrder: noop,
  revertLayout: noop,
  saveLayout: () => Promise.resolve(),
});
export const ViewLayoutActionsProvider = ViewLayoutActionsContext.Provider;

export function useViewLayoutActions(): ViewLayoutActions {
  return useContext(ViewLayoutActionsContext);
}
